import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'react-calendar/dist/Calendar.css';
import CreateAppointment from './CreateAppointment';
import DataTable from 'react-data-table-component-with-filter';
import './Appointments.css';
import '../Tables.css';
import Sidebar from '../Sidebar';
import InternalHeader from '../InternalHeader';
import CustomPagination from '../CustomPagination/CustomPagination';
import Logging from '../Logging/Logging';
import { FaCalendar, FaTrash, FaCog, FaUpload, FaArchive } from 'react-icons/fa';
import { format } from 'date-fns';
import Logger from '../Logger/Logger';
import LinkMap from '../LinkMap/LinkMap';
import FilterAppointments from '../Filter/FilterAppointments';
import AccessControl from '../AccessControl';

const AppointmentsTable = () => {
    const navigate = useNavigate();
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [date, setDate] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [logs, setLogs] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewOption, setViewOption] = useState('all'); // State for filtering by status
    const totalRecords = appointments.length;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]); // Track selected rows
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
    const linkData = [
        { label: 'Appointments', path: '/appointments' },
        { label: 'Appointments Table', path: '/appointments-table' },
    ];

    const logChange = (action, details) => {
        setLogs(prevLogs => [...prevLogs, { timestamp: new Date().toLocaleString(), action, details }]);
    };


    const handleActionsToggle = () => {
        setIsActionsDropdownOpen(!isActionsDropdownOpen);
    };

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const handleDateChange = (date) => {
        setDate(date);
    };

    // Fetch appointments from the API
    const fetchAppointments = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/appointments/`, {
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Failed to fetch appointments');
            }
            const data = await response.json();
            setAppointments(data);
            logChange('Fetched', `Loaded ${data.length} appointments.`);
        } catch (error) {
            console.error('Error fetching appointments:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, []);

    // Columns for the DataTable
    const columns = [
        { name: 'Patient ID', selector: row => row.patientID, sortable: true, filterable: true },
        { name: 'Patient Name', selector: row => row.patientName, sortable: true, filterable: true },
        { name: 'Email', selector: row => row.email, sortable: true, filterable: true },
        { name: 'Reason', selector: row => row.reason, sortable: true, filterable: true },
        {
            name: 'Date',
            selector: row => format(new Date(row.date), 'MMMM dd, yyyy'),
            sortable: true,
            filterable: true
        },
        { name: 'Time', selector: row => row.startTime, sortable: true, filterable: true },
        { name: 'Dentist', selector: row => row.dentist, sortable: true, filterable: true },
        { name: 'Status', selector: row => row.status || 'Pending', sortable: true, filterable: true },
    ];

    // Handle pagination changes
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    // Handle rows per page changes
    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(Number(newRowsPerPage));
        setCurrentPage(1);
    };

    // Handle search input changes
    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    // Filter appointments based on search term and view option
    const filteredData = appointments.filter(appointment => {
        // Filter by viewOption (status or archived)
        if (viewOption === 'Checked In') {
            return appointment.status === 'Checked In';
        } else if (viewOption === 'Completed') {
            return appointment.status === 'Completed';
        } else if (viewOption === 'Cancelled') {
            return appointment.status === 'Cancelled';
        } else if (viewOption === 'Booked') {
            return appointment.status === 'Booked';
        } else if (viewOption === 'Archived') {
            return appointment.isArchived === true; // Show only archived appointments
        } else {
            return appointment.isArchived !== true; // Hide archived appointments by default
        }
    }).filter(appointment =>
        // Filter by search term
        (appointment.patientName && appointment.patientName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (appointment.email && appointment.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (appointment.reason && appointment.reason.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    // Paginate the filtered data
    const paginatedData = filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    // Calculate total pages for pagination
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    // Handle row click to navigate to appointment details
    const handleRowClick = (row) => {
        navigate(`/appointment-details/${row._id}`);
    };

    // Handle archiving selected appointments
    const handleDeleteSelected = async () => {
        if (selectedRows.length === 0) {
            alert('No appointments selected.');
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to archive the selected appointments?');
        if (!confirmArchive) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/appointments/archive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ appointmentIds: selectedRows }),
            });

            if (!response.ok) {
                throw new Error('Failed to archive appointments');
            }

            const data = await response.json();
            console.log('Appointments archived successfully:', data);
            alert('Appointments archived successfully');
            // Refresh the appointments list
            fetchAppointments();

            // Clear selected rows
            setSelectedRows([]);

        } catch (error) {
            console.error('Error archiving appointments:', error);
            setError('Failed to archive appointments. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleUnDeleteSelected = async () => {
        if (selectedRows.length === 0) {
            alert('No appointments selected.');
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to unarchive the selected appointments?');
        if (!confirmArchive) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/appointments/unarchive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ appointmentIds: selectedRows }),
            });

            if (!response.ok) {
                throw new Error('Failed to unarchive appointments');
            }

            const data = await response.json();
            console.log('Appointments unarchived successfully:', data);
            alert('Appointments unarchived successfully');
            // Refresh the appointments list
            fetchAppointments();

            // Clear selected rows
            setSelectedRows([]);

        } catch (error) {
            console.error('Error unarchiving appointments:', error);
            setError('Failed to unarchive appointments. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle generating CSV (placeholder function)
    const handleGenerateCSV = () => {
        // Add logic to generate CSV
    };

    // Handle selected rows change
    const handleSelectedRowsChange = ({ selectedRows }) => {
        setSelectedRows(selectedRows.map(row => row._id));
    };

    // Handle closing the modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    // Handle opening the modal
    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    // Custom styles for the DataTable
    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    return (
        <AccessControl>
            <div className={`appointments-page ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
                <InternalHeader moduleName="Appointments" toggleSidebar={toggleSidebar} />
                <div className="appointments-container">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="appointments-container2">
                        <LinkMap
                            links={linkData}
                            moduleName="Appointments"
                            moduleIcon="calendar"
                        />
                        <div className="data-table-header">
                            <div className="flex-div">
                                <a href="/create-appointment">
                                    <button className="create-button">New</button>
                                </a>
                            </div>
                            {isModalOpen && (
                                <CreateAppointment onClose={handleCloseModal} fetchAppointments={fetchAppointments} />
                            )}
                            {/* Conditionally render Filter or Actions dropdown */}
                            {selectedRows.length > 0 ? (
                                <div className="actions-dropdown-container">
                                    <button onClick={handleActionsToggle} className="actions-button">
                                        <FaCog />  Actions
                                    </button>
                                    {isActionsDropdownOpen && (
                                        <div className="actions-dropdown-options1">
                                            {/* Always show Export Records button */}
                                            {/* <div onClick={handleExport}>
                                            <FaUpload /> Export Records
                                        </div> */}

                                            {/* Show Archive button only when viewOption is 'all' or 'active' */}
                                            {(viewOption === 'all' || viewOption === 'active') && (
                                                <div onClick={handleDeleteSelected}>
                                                    <FaArchive /> Archive
                                                </div>
                                            )}

                                            {/* Show Unarchive button only when viewOption is 'archived' */}
                                            {viewOption === 'Archived' && (
                                                <div onClick={handleUnDeleteSelected}>
                                                    <FaArchive /> Unarchive
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <FilterAppointments
                                    searchText={searchTerm}
                                    setSearchText={setSearchTerm}
                                    viewOption={viewOption}
                                    setViewOption={setViewOption}
                                />
                            )}
                            <div className="column-div">
                                <CustomPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={handlePageChange}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                    totalRecords={filteredData.length}
                                />
                                <a href="/appointments">
                                    <button className="view-toggle-button"><FaCalendar /></button>
                                </a>
                            </div>
                        </div>
                        <div className="table-form">
                            <div>
                                <DataTable
                                    title="Appointments"
                                    columns={columns}
                                    data={paginatedData}
                                    paginationPerPage={rowsPerPage}
                                    paginationTotalRows={filteredData.length}
                                    onSelectedRowsChange={handleSelectedRowsChange}
                                    selectableRows
                                    selectableRowsHighlight
                                    highlightOnHover
                                    onRowClicked={handleRowClick}
                                    customStyles={customStyles}
                                />
                            </div>
                        </div>
                        <div className="log-display-section">
                            <h2>Logs</h2>
                            <div className="log-container">
                                <ul>
                                    {logs.map((log, index) => (
                                        <li key={index}>{log.timestamp} - {log.action}: {log.details}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default AppointmentsTable;